/**
 * AgendaWizardSettings Component
 * Super Admin panel: manage questions, active toggle, and usage stats.
 */
import React, { useState, useEffect, useCallback } from 'react';
import writeXlsxFile from 'write-excel-file/browser';
import ApiService from '../services/ApiService';

const TYPE_LABELS = {
  categories: '📂 Categories',
  time_window: '🕐 Time Window',
  text: '✏️ Free Text',
};

const BUILT_IN_KEYS = ['q1', 'q2', 'q3'];

const formatDateInput = (date) => date.toISOString().slice(0, 10);

const AgendaWizardSettings = () => {
  const [wizardActive, setWizardActive] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState({ opens: 0, completions: 0 });
  const [statsStartDate, setStatsStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return formatDateInput(date);
  });
  const [statsEndDate, setStatsEndDate] = useState(() => formatDateInput(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', sublabel: '', placeholder: '', sort_order: '' });

  // Add question state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ label: '', sublabel: '', placeholder: '' });
  const [addError, setAddError] = useState('');

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [questionsRes, statsRes, settingsRes] = await Promise.all([
        ApiService.get('/super-admin/agenda-wizard/questions'),
        ApiService.get('/super-admin/agenda-wizard/stats'),
        ApiService.get('/settings/public'),
      ]);
      setQuestions(questionsRes.data || []);
      setStats(statsRes.data || { opens: 0, completions: 0 });
      setWizardActive(settingsRes.agenda_wizard_active !== 'false');
    } catch (err) {
      setError(err.message || 'Failed to load Agenda Wizard settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Active toggle ──────────────────────────────────────────────────────────
  const handleToggleActive = async () => {
    const newValue = !wizardActive;
    try {
      await ApiService.put('/super-admin/settings/agenda_wizard_active', {
        value: String(newValue),
      });
      setWizardActive(newValue);
      showSuccess(`Agenda Wizard ${newValue ? 'activated' : 'deactivated'}.`);
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
  };

  // ── Stats reset ────────────────────────────────────────────────────────────
  const handleResetStats = async () => {
    if (!confirm('Reset all opens and completions counters? This cannot be undone.')) return;
    try {
      await ApiService.delete('/super-admin/agenda-wizard/stats');
      setStats({ opens: 0, completions: 0 });
      showSuccess('Stats reset.');
    } catch (err) {
      setError(err.message || 'Failed to reset stats');
    }
  };

  // ── Download stats ─────────────────────────────────────────────────────────
  // completionRate must be computed before this handler so the closure captures
  // the correct value at call time.
  const _completionRate = stats.opens > 0
    ? Math.round((stats.completions / stats.opens) * 100)
    : 0;

  const handleDownloadStats = async () => {
    const row = {
      from: statsStartDate,
      to: statsEndDate,
      opens: stats.opens,
      completions: stats.completions,
      rate: _completionRate,
      generatedAt: new Date().toLocaleString(),
    };
    await writeXlsxFile([row], {
      schema: [
        { column: 'Report Period From', type: String, value: r => r.from, width: 22 },
        { column: 'Report Period To',   type: String, value: r => r.to,   width: 18 },
        { column: 'Wizard Opens',       type: Number, value: r => r.opens, width: 14 },
        { column: 'Completions',        type: Number, value: r => r.completions, width: 14 },
        { column: 'Completion Rate (%)', type: Number, value: r => r.rate, width: 22 },
        { column: 'Generated At',       type: String, value: r => r.generatedAt, width: 26 },
      ],
      fileName: `agenda-wizard-usage_${statsStartDate}_${statsEndDate}.xlsx`,
      sheet: 'Usage Statistics',
    });
    showSuccess('Usage statistics downloaded.');
  };

  // ── Question toggle active ─────────────────────────────────────────────────
  const handleToggleQuestion = async (q) => {
    try {
      const res = await ApiService.put(`/super-admin/agenda-wizard/questions/${q.id}`, {
        active: !q.active,
      });
      setQuestions(prev => prev.map(x => (x.id === q.id ? res.data : x)));
      showSuccess(`Question "${q.label}" ${!q.active ? 'activated' : 'deactivated'}.`);
    } catch (err) {
      setError(err.message || 'Failed to update question');
    }
  };

  // ── Start edit ─────────────────────────────────────────────────────────────
  const startEdit = (q) => {
    setEditingId(q.id);
    setEditForm({
      label: q.label,
      sublabel: q.sublabel || '',
      placeholder: q.placeholder || '',
      sort_order: String(q.sort_order),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ label: '', sublabel: '', placeholder: '', sort_order: '' });
  };

  const handleSaveEdit = async (q) => {
    if (!editForm.label.trim()) {
      setError('Question label cannot be empty.');
      return;
    }
    try {
      const payload = {
        label: editForm.label.trim(),
        sublabel: editForm.sublabel.trim() || null,
        placeholder: editForm.placeholder.trim() || null,
        sort_order: parseInt(editForm.sort_order, 10) || q.sort_order,
      };
      const res = await ApiService.put(`/super-admin/agenda-wizard/questions/${q.id}`, payload);
      setQuestions(prev =>
        prev.map(x => (x.id === q.id ? res.data : x))
            .sort((a, b) => a.sort_order - b.sort_order)
      );
      cancelEdit();
      showSuccess('Question updated.');
    } catch (err) {
      setError(err.message || 'Failed to save question');
    }
  };

  // ── Delete question ────────────────────────────────────────────────────────
  const handleDelete = async (q) => {
    if (BUILT_IN_KEYS.includes(q.step_key)) {
      setError('Built-in questions cannot be deleted. Toggle them inactive instead.');
      return;
    }
    if (!confirm(`Delete question "${q.label}"?`)) return;
    try {
      await ApiService.delete(`/super-admin/agenda-wizard/questions/${q.id}`);
      setQuestions(prev => prev.filter(x => x.id !== q.id));
      showSuccess('Question deleted.');
    } catch (err) {
      setError(err.message || 'Failed to delete question');
    }
  };

  // ── Add question ───────────────────────────────────────────────────────────
  const handleAddQuestion = async (e) => {
    e.preventDefault();
    setAddError('');
    if (!addForm.label.trim()) {
      setAddError('Label is required.');
      return;
    }
    try {
      const res = await ApiService.post('/super-admin/agenda-wizard/questions', {
        label: addForm.label.trim(),
        sublabel: addForm.sublabel.trim() || null,
        placeholder: addForm.placeholder.trim() || null,
      });
      setQuestions(prev => [...prev, res.data].sort((a, b) => a.sort_order - b.sort_order));
      setAddForm({ label: '', sublabel: '', placeholder: '' });
      setShowAddForm(false);
      showSuccess('Question added.');
    } catch (err) {
      setAddError(err.message || 'Failed to add question');
    }
  };

  // ── Completion rate (alias used in JSX) ────────────────────────────────────
  const completionRate = _completionRate;

  if (loading) {
    return <div style={{ padding: '2rem', color: '#6b7280' }}>Loading Agenda Wizard settings...</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '860px' }}>
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', marginBottom: '16px', fontSize: '14px' }}>
          {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 'bold' }}>×</button>
        </div>
      )}
      {success && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '12px 16px', color: '#16a34a', marginBottom: '16px', fontSize: '14px' }}>
          {success}
        </div>
      )}

      {/* ── Active / Inactive Toggle ─────────────────────────────────────── */}
      <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>
              🪄 Agenda Wizard Feature
            </h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
              Controls whether the Agenda Wizard button and panel are visible on the homepage.
            </p>
          </div>
          <button
            onClick={handleToggleActive}
            style={{
              padding: '10px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '700',
              fontSize: '14px',
              background: wizardActive ? '#16a34a' : '#dc2626',
              color: '#fff',
              minWidth: '110px',
              transition: 'background 0.2s',
            }}
          >
            {wizardActive ? '🟢 Active' : '🔴 Inactive'}
          </button>
        </div>
      </section>

      {/* ── Usage Stats ──────────────────────────────────────────────────── */}
      <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>📊 Usage Statistics</h3>
          <button
            onClick={handleResetStats}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: '600' }}
          >
            Reset Counters
          </button>
        </div>
        {/* ── Download strip ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#374151', fontWeight: '600' }}>
            From
            <input
              type="date"
              value={statsStartDate}
              onChange={(e) => setStatsStartDate(e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#374151', fontWeight: '600' }}>
            To
            <input
              type="date"
              value={statsEndDate}
              onChange={(e) => setStatsEndDate(e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
            />
          </label>
          <button
            onClick={handleDownloadStats}
            style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', background: '#005f87', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
          >
            📥 Download Report (.xlsx)
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          <div style={{ background: '#f0f9ff', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0284c7', lineHeight: 1 }}>{stats.opens}</div>
            <div style={{ fontSize: '12px', color: '#0369a1', fontWeight: '600', marginTop: '6px' }}>👁 Wizard Opens</div>
          </div>
          <div style={{ background: '#f0fdf4', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#16a34a', lineHeight: 1 }}>{stats.completions}</div>
            <div style={{ fontSize: '12px', color: '#15803d', fontWeight: '600', marginTop: '6px' }}>✅ Completions</div>
          </div>
          <div style={{ background: '#fefce8', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#ca8a04', lineHeight: 1 }}>{completionRate}%</div>
            <div style={{ fontSize: '12px', color: '#a16207', fontWeight: '600', marginTop: '6px' }}>📈 Completion Rate</div>
          </div>
        </div>
      </section>

      {/* ── Questions Manager ─────────────────────────────────────────────── */}
      <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
            ❓ Wizard Questions ({questions.length})
          </h3>
          <button
            onClick={() => { setShowAddForm(v => !v); setAddError(''); setAddForm({ label: '', sublabel: '', placeholder: '' }); }}
            style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#005f87', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}
          >
            {showAddForm ? '✕ Cancel' : '+ Add Question'}
          </button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <form
            onSubmit={handleAddQuestion}
            style={{ background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}
          >
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>
              New questions are <strong>free text</strong> type — the user's answer contributes to keyword-based session matching.
            </p>
            {addError && <p style={{ color: '#dc2626', fontSize: '13px', marginBottom: '8px' }}>{addError}</p>}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                Question label <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={addForm.label}
                onChange={e => setAddForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g. What technologies are you most excited about?"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                required
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Sublabel / hint (optional)</label>
              <input
                type="text"
                value={addForm.sublabel}
                onChange={e => setAddForm(f => ({ ...f, sublabel: e.target.value }))}
                placeholder="e.g. A few words to help us personalize your agenda"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Placeholder text (optional)</label>
              <input
                type="text"
                value={addForm.placeholder}
                onChange={e => setAddForm(f => ({ ...f, placeholder: e.target.value }))}
                placeholder="e.g. e.g. React, cloud architecture, agile..."
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" style={{ padding: '8px 18px', borderRadius: '7px', border: 'none', background: '#005f87', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                Save Question
              </button>
              <button type="button" onClick={() => setShowAddForm(false)} style={{ padding: '8px 18px', borderRadius: '7px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Questions list */}
        {questions.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center', padding: '2rem 0' }}>No questions found.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {questions.map(q => (
              <div
                key={q.id}
                style={{
                  border: `1px solid ${q.active ? '#d1d5db' : '#fecaca'}`,
                  borderRadius: '10px',
                  padding: '14px 16px',
                  background: q.active ? '#fff' : '#fff5f5',
                  opacity: q.active ? 1 : 0.8,
                }}
              >
                {editingId === q.id ? (
                  /* Edit form */
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '3px' }}>Label *</label>
                        <input
                          type="text"
                          value={editForm.label}
                          onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
                          style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '3px' }}>Sort Order</label>
                        <input
                          type="number"
                          value={editForm.sort_order}
                          onChange={e => setEditForm(f => ({ ...f, sort_order: e.target.value }))}
                          style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '3px' }}>Sublabel</label>
                      <input
                        type="text"
                        value={editForm.sublabel}
                        onChange={e => setEditForm(f => ({ ...f, sublabel: e.target.value }))}
                        style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>
                    {q.question_type === 'text' && (
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '3px' }}>Placeholder</label>
                        <input
                          type="text"
                          value={editForm.placeholder}
                          onChange={e => setEditForm(f => ({ ...f, placeholder: e.target.value }))}
                          style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                        />
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleSaveEdit(q)}
                        style={{ padding: '7px 16px', borderRadius: '6px', border: 'none', background: '#005f87', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        style={{ padding: '7px 16px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display row */
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ flexShrink: 0, color: '#9ca3af', fontSize: '13px', minWidth: '28px', paddingTop: '2px' }}>
                      #{q.sort_order}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '3px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{q.label}</span>
                        <span style={{ fontSize: '11px', background: '#e5e7eb', borderRadius: '4px', padding: '2px 7px', color: '#374151', fontWeight: '600', flexShrink: 0 }}>
                          {TYPE_LABELS[q.question_type] || q.question_type}
                        </span>
                        {BUILT_IN_KEYS.includes(q.step_key) && (
                          <span style={{ fontSize: '11px', background: '#dbeafe', borderRadius: '4px', padding: '2px 7px', color: '#1d4ed8', fontWeight: '600', flexShrink: 0 }}>built-in</span>
                        )}
                        {!q.active && (
                          <span style={{ fontSize: '11px', background: '#fee2e2', borderRadius: '4px', padding: '2px 7px', color: '#dc2626', fontWeight: '600', flexShrink: 0 }}>inactive</span>
                        )}
                      </div>
                      {q.sublabel && <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>{q.sublabel}</div>}
                      {q.placeholder && <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>Placeholder: {q.placeholder}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button
                        onClick={() => startEdit(q)}
                        style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#374151' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleQuestion(q)}
                        style={{
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: `1px solid ${q.active ? '#fca5a5' : '#86efac'}`,
                          background: '#fff',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: q.active ? '#dc2626' : '#16a34a',
                        }}
                      >
                        {q.active ? 'Deactivate' : 'Activate'}
                      </button>
                      {!BUILT_IN_KEYS.includes(q.step_key) && (
                        <button
                          onClick={() => handleDelete(q)}
                          style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid #fca5a5', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#dc2626' }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AgendaWizardSettings;
